/**
 * Self-Cert Entity XML Generator - COMPLETE VERSION
 * Updated implementation with ALL form fields mapped systematically
 */

import { SelfCertEntityService } from "../../../services/SelfCertEntityService";
import { SelfCertEntityFormModel } from "../../../Models/SelfCertEntityModels";
import { formatDateForXML } from "../../../Utility/utility";
import { getPDFBase64 } from "./generateW8BENXML";

export async function generateSelfCertEntityXML(data: any): Promise<string> {
  // Check if form data needs mapping
  let formModel: SelfCertEntityFormModel;
  let isMapDone = data?.isMapDone || false;

  if (!isMapDone) {
    console.log(
      "🧱 SelfCert Entity XML: Data not mapped, using SelfCertEntityService.fromApiResponse"
    );
    formModel = SelfCertEntityService.fromApiResponse(data);
  } else {
    console.log("🧱 SelfCert Entity XML: Data already mapped, using directly");
    formModel = new SelfCertEntityFormModel(data);
  }

  // Helper function to get field values
  const getValue = (fieldName: string): string => {
    const value = formModel[fieldName] || "";
    return value;
  };

  // Helper function to convert yes/no values to true/false
  const convertYesNoToBoolean = (fieldName: string): string => {
    const value = formModel[fieldName];
    if (value === true || value === "yes" || value === "Yes") {
      return "true";
    }
    if (
      value === false ||
      value === "no" ||
      value === "No" ||
      value === "" ||
      value === null ||
      value === undefined
    ) {
      return "false";
    }
    return "false"; // Default to false for any other value
  };

  // Generate original file XML if viewPdf is provided
  const generateOriginalFileXml = async () => {
    const viewPdfUrl = getValue("viewPdf");
    if (viewPdfUrl) {
      try {
        const originalFileContent = await getPDFBase64(viewPdfUrl, formModel["fileImportId"]);
        const fileName = viewPdfUrl.split("/").pop() || "original_document.pdf";

        return `
        <OriginalFile>
          <Name>${fileName}</Name>
          <Data>${originalFileContent}</Data>
        </OriginalFile>`;
      } catch (error) {
        console.error("Error converting original PDF to base64:", error);
        return "<OriginalFile></OriginalFile>";
      }
    }
    return "<OriginalFile></OriginalFile>";
  };

  const originalFileXml = await generateOriginalFileXml();

  // Generate XML using the user-provided template with ALL form field mappings
  const xml = `<?xml version="1.0" encoding="UTF-8"?>
  <Deloitte_US_Entity_2024>
	<BusinessUnit></BusinessUnit>
  <UniqueIdentifier>${getValue("UniqueIdentifier")}</UniqueIdentifier>
	<CommonInfo>
		<Business>
			<Name>${getValue("SC_Entity_Section_1_entity_name")}</Name>
			<Country>${getValue("SC_Entity_Section_1_org_country")}</Country>
			<CountryOther>${getValue("SC_Entity_Section_1_org_country_other")}</CountryOther>
		</Business>

		<PermanentAddress>
			<Line1>${getValue("SC_Entity_Section_1_current_residence_address_street")}</Line1>
			<Line2>${getValue("SC_Entity_Section_1_current_residence_address_line2")}</Line2>
			<Line3>${getValue("SC_Entity_Section_1_current_residence_address_line3")}</Line3>
			<City_Town>${getValue("SC_Entity_Section_1_current_residence_address_city")}</City_Town>
			<State_Province>${getValue("SC_Entity_Section_1_current_residence_address_state")}</State_Province>
			<Country>${getValue("SC_Entity_Section_1_current_residence_address_country")}</Country>
			<CountryOther>${getValue("SC_Entity_Section_1_current_residence_address_country_other")}</CountryOther>
			<Zip_PostalCode>${getValue("SC_Entity_Section_1_current_residence_address_postal")}</Zip_PostalCode>
			<IsCare_of_address>${convertYesNoToBoolean("SC_Entity_Section_1_current_residence_care_of")}</IsCare_of_address>
			<IsP_O_Box>${convertYesNoToBoolean("SC_Entity_Section_1_current_residence_po_box")}</IsP_O_Box>
			<IsRuralRoute>${convertYesNoToBoolean("SC_Entity_Section_1_current_residence_rural_route")}</IsRuralRoute>
		</PermanentAddress>

		<MailingAddress>
			<Line1>${getValue("SC_Entity_Section_1_mailing_address_street")}</Line1>
			<Line2>${getValue("SC_Entity_Section_1_mailing_address_line2")}</Line2>
			<Line3>${getValue("SC_Entity_Section_1_mailing_address_line3")}</Line3>
			<City_Town>${getValue("SC_Entity_Section_1_mailing_address_city")}</City_Town>
			<State_Province>${getValue("SC_Entity_Section_1_mailing_address_state")}</State_Province>
			<Country>${getValue("SC_Entity_Section_1_mailing_address_country")}</Country>
			<CountryOther>${getValue("SC_Entity_Section_1_mailing_address_country_other")}</CountryOther>
			<Zip_PostalCode>${getValue("SC_Entity_Section_1_mailing_address_postal")}</Zip_PostalCode>
			<IsCare_of_address>${convertYesNoToBoolean("SC_Entity_Section_1_mailing_care_of")}</IsCare_of_address>
			<IsP_O_Box>${convertYesNoToBoolean("SC_Entity_Section_1_mailing_po_box")}</IsP_O_Box>
			<IsRuralRoute>${convertYesNoToBoolean("SC_Entity_Section_1_mailing_rural_route")}</IsRuralRoute>
		</MailingAddress>

	</CommonInfo>
	
	<!-- Section II - Tax Residency -->
	<TaxResidencyCountry>${getValue("SC_Entity_Section_2_tax_identification_country")}</TaxResidencyCountry>
	<TaxResidencyTINType>${getValue("SC_Entity_Section_2_tax_reference_number_type")}</TaxResidencyTINType>
	<TaxResidencyTIN>${getValue("SC_Entity_Section_2_tax_id")}</TaxResidencyTIN>
	<cbTaxResidencyTINNotAvailable>${convertYesNoToBoolean("SC_Entity_Section_2_unable_to_procure_a_tin")}</cbTaxResidencyTINNotAvailable>
	<TaxResidencyTINNotAvailableReason>${getValue("SC_Entity_Section_2_reason_for_non_tin")}</TaxResidencyTINNotAvailableReason>
	<cbTaxResidencyYes>${convertYesNoToBoolean("SC_Entity_Section_2_sole_residency_yes")}</cbTaxResidencyYes>
	<cbTaxResidencyNo>${convertYesNoToBoolean("SC_Entity_Section_2_sole_residency_no")}</cbTaxResidencyNo>

	<!-- Additional Tax Jurisdictions Table -->
	<TaxJurisdiction2>
		<Country>${getValue("sc_11 Jurisdiction of Tax Residence2")}</Country>
		<TIN>${getValue("sc_11 Tax Identification Number2")}</TIN>
		<TINType>${getValue("sc_11 Tax Reference Number Type2")}</TINType>
		<TINNotAvailableReason>${getValue("sc_11 non-availability of TIN 2")}</TINNotAvailableReason>
	</TaxJurisdiction2>
	
	<TaxJurisdiction3>
		<Country>${getValue("sc_11 Jurisdiction of Tax Residence3")}</Country>
		<TIN>${getValue("sc_11 Tax Identification Number3")}</TIN>
		<TINType>${getValue("sc_11 Tax Reference Number Type3")}</TINType>
		<TINNotAvailableReason>${getValue("sc_11 non-availability of TIN 3")}</TINNotAvailableReason>
	</TaxJurisdiction3>

	<TaxJurisdiction4>
		<Country>${getValue("sc_11 Jurisdiction of Tax Residence4")}</Country>
		<TIN>${getValue("sc_11 Tax Identification Number4")}</TIN>
		<TINType>${getValue("sc_11 Tax Reference Number Type4")}</TINType>
		<TINNotAvailableReason>${getValue("sc_11 non-availability of TIN 4")}</TINNotAvailableReason>
	</TaxJurisdiction4>

	<!-- Section III - FATCA/CRS Classification -->
	<!-- FATCA Classification -->
	<FATCA_FinancialInstitution>${convertYesNoToBoolean("SC_Entity_Section_3_financial_institution")}</FATCA_FinancialInstitution>
	<FATCA_GIIN>${getValue("SC_Entity_Section_3_fatca_purposes")}</FATCA_GIIN>
	<FATCA_ReportingFI>${convertYesNoToBoolean("SC_Entity_Section_3_reporting_financial_institution_under_crs")}</FATCA_ReportingFI>
	<FATCA_NonReportingFI>${convertYesNoToBoolean("SC_Entity_Section_3_non_financial_institution_under_crs")}</FATCA_NonReportingFI>
	<FATCA_GovernmentalEntity>${convertYesNoToBoolean("SC_Entity_Section_3_goverment_entity")}</FATCA_GovernmentalEntity>
	<FATCA_InternationalOrganization>${convertYesNoToBoolean("SC_Entity_Section_3_international_org")}</FATCA_InternationalOrganization>
	<FATCA_CentralBank>${convertYesNoToBoolean("SC_Entity_Section_3_central_bank")}</FATCA_CentralBank>
	<FATCA_BroadParticipationRetirementFund>${convertYesNoToBoolean("SC_Entity_Section_3_broad_participation_retirement_fund")}</FATCA_BroadParticipationRetirementFund>
	<FATCA_NarrowParticipation>${convertYesNoToBoolean("SC_Entity_Section_3_narrow_participation_retirement_fund")}</FATCA_NarrowParticipation>
	<FATCA_PensionFund>${convertYesNoToBoolean("SC_Entity_Section_3_pension_fund")}</FATCA_PensionFund>
	<FATCA_ExemptCollective>${convertYesNoToBoolean("SC_Entity_Section_3_exempt_collective_investment")}</FATCA_ExemptCollective>
	<FATCA_TrustTrustee>${convertYesNoToBoolean("SC_Entity_Section_3_trust_trustee")}</FATCA_TrustTrustee>
	<FATCA_QualifiedCreditCardIssuer>${convertYesNoToBoolean("SC_Entity_Section_3_qualified_credit_card_issuer")}</FATCA_QualifiedCreditCardIssuer>
	<FATCA_OtherEntity>${convertYesNoToBoolean("SC_Entity_Section_3_other_entity")}</FATCA_OtherEntity>
	<FATCA_OtherEntityText>${getValue("SC_Entity_Section_3_other_entity_specify")}</FATCA_OtherEntityText>
	
	<!-- CRS Classification -->
	<CRS_FinancialInstitution>${convertYesNoToBoolean("SC_Entity_Section_3_financial_institution")}</CRS_FinancialInstitution>
	<CRS_GIIN>${getValue("SC_Entity_Section_3_fatca_purposes")}</CRS_GIIN>
	<CRS_ReportingFI>${convertYesNoToBoolean("SC_Entity_Section_3_reporting_financial_institution_under_crs")}</CRS_ReportingFI>
	<CRS_NonReportingFI>${convertYesNoToBoolean("SC_Entity_Section_3_non_financial_institution_under_crs")}</CRS_NonReportingFI>
	<CRS_GovernmentalEntity>${convertYesNoToBoolean("SC_Entity_Section_3_goverment_entity")}</CRS_GovernmentalEntity>
	<CRS_InternationalOrganization>${convertYesNoToBoolean("SC_Entity_Section_3_international_org")}</CRS_InternationalOrganization>
	<CRS_CentralBank>${convertYesNoToBoolean("SC_Entity_Section_3_central_bank")}</CRS_CentralBank>
	<CRS_BroadParticipationRetirementFund>${convertYesNoToBoolean("SC_Entity_Section_3_broad_participation_retirement_fund")}</CRS_BroadParticipationRetirementFund>
	<CRS_NarrowParticipation>${convertYesNoToBoolean("SC_Entity_Section_3_narrow_participation_retirement_fund")}</CRS_NarrowParticipation>
	<CRS_PensionFund>${convertYesNoToBoolean("SC_Entity_Section_3_pension_fund")}</CRS_PensionFund>
	<CRS_ExemptCollective>${convertYesNoToBoolean("SC_Entity_Section_3_exempt_collective_investment")}</CRS_ExemptCollective>
	<CRS_TrustTrustee>${convertYesNoToBoolean("SC_Entity_Section_3_trust_trustee")}</CRS_TrustTrustee>
	<CRS_QualifiedCreditCardIssuer>${convertYesNoToBoolean("SC_Entity_Section_3_qualified_credit_card_issuer")}</CRS_QualifiedCreditCardIssuer>
	<CRS_OtherEntity>${convertYesNoToBoolean("SC_Entity_Section_3_other_entity")}</CRS_OtherEntity>
	<CRS_OtherEntityText>${getValue("SC_Entity_Section_3_other_entity_specify")}</CRS_OtherEntityText>

	<!-- Investment Entity Classifications -->
	<InvestmentEntity_NonParticipatingJurisdiction>${convertYesNoToBoolean("SC_Entity_Section_3_non_financial_institution_under_crs_resident")}</InvestmentEntity_NonParticipatingJurisdiction>
	<InvestmentEntity_ManagedByCompanyListed>${convertYesNoToBoolean("SC_Entity_Section_3_investment_entity_managed_by_company_listed")}</InvestmentEntity_ManagedByCompanyListed>
	<InvestmentEntity_ManagedByFinancialInstitution>${convertYesNoToBoolean("SC_Entity_Section_3_investment_entity")}</InvestmentEntity_ManagedByFinancialInstitution>
	<InvestmentEntity_WidelyHeldCIV>${convertYesNoToBoolean("SC_Entity_Section_3_investment_entity_managed_by_widely_held_civ")}</InvestmentEntity_WidelyHeldCIV>
	<InvestmentEntity_PensionFundTrust>${convertYesNoToBoolean("SC_Entity_Section_3_investment_entity_managed_by_pension_fund")}</InvestmentEntity_PensionFundTrust>
	<InvestmentEntity_Other>${convertYesNoToBoolean("SC_Entity_Section_3_other_investment_entity")}</InvestmentEntity_Other>
	<OtherFinancialInstitution>${convertYesNoToBoolean("SC_Entity_Section_3_non_financial_institution")}</OtherFinancialInstitution>

	<!-- NFE Classification -->
	<NFE_ActiveNFE>${convertYesNoToBoolean("SC_Entity_Section_3_active_nfe")}</NFE_ActiveNFE>
	<NFE_RegularlyTradedCorporation>${convertYesNoToBoolean("SC_Entity_Section_3_regularly_traded_corporation")}</NFE_RegularlyTradedCorporation>
	<NFE_StockExchangeName>${getValue("SC_Entity_Section_3_stock_exchange_name")}</NFE_StockExchangeName>
	<NFE_RegularlyTradedCorporationName>${getValue("SC_Entity_Section_3_regularly_traded_corporation_name")}</NFE_RegularlyTradedCorporationName>
	<NFE_GovernmentEntity>${convertYesNoToBoolean("SC_Entity_Section_3_active_nfe_goverment_entity")}</NFE_GovernmentEntity>
	<NFE_InternationalOrganization>${convertYesNoToBoolean("SC_Entity_Section_3_active_nfe_international_org")}</NFE_InternationalOrganization>
	<NFE_Other>${convertYesNoToBoolean("SC_Entity_Section_3_active_nfe_other")}</NFE_Other>
	<NFE_PassiveNFE>${convertYesNoToBoolean("SC_Entity_Section_3_passive_nfe")}</NFE_PassiveNFE>

	<!-- Section IV - Controlling Persons -->
	<ControllingPersons>
		<List>
			<Person1>${getValue("SC_Entity_Controlling_person_1")}</Person1>
			<Person2>${getValue("SC_Entity_Controlling_person_2")}</Person2>
			<Person3>${getValue("SC_Entity_Controlling_person_3")}</Person3>
			<Person4>${getValue("SC_Entity_Controlling_person_4")}</Person4>
			<Person5>${getValue("SC_Entity_Controlling_person_5")}</Person5>
			<Person6>${getValue("SC_Entity_Controlling_person_6")}</Person6>
			<Person7>${getValue("SC_Entity_Controlling_person_7")}</Person7>
			<Person8>${getValue("SC_Entity_Controlling_person_8")}</Person8>
			<Person9>${getValue("SC_Entity_Controlling_person_9")}</Person9>
			<Person10>${getValue("SC_Entity_Controlling_person_10")}</Person10>
		</List>
		
		<PersonDetails>
			<Title>${getValue("SC-Entity-Controlling_person_title")}</Title>
			<FirstName>${getValue("SC-Entity-Controlling_person_first_name")}</FirstName>
			<MiddleName>${getValue("SC-Entity-Controlling_person_middle_name")}</MiddleName>
			<LastName>${getValue("SC-Entity-Controlling_person_last_name")}</LastName>
			<DateOfBirth>${getValue("SC-Entity-Controlling_person_dob")}</DateOfBirth>
			<CityOfBirth>${getValue("SC-Entity-Controlling_person_city_of_birth")}</CityOfBirth>
			<CountryOfBirth>${getValue("SC-Entity-Controlling_person_country_of_birth")}</CountryOfBirth>
			
			<CurrentAddress>
				<Street>${getValue("SC-Entity-Controlling_person_address_street")}</Street>
				<Street2>${getValue("SC-Entity-Controlling_person_address_street2")}</Street2>
				<City>${getValue("SC-Entity-Controlling_person_address_city")}</City>
				<Country>${getValue("SC-Entity-Controlling_person_address_country")}</Country>
				<PostalCode>${getValue("SC-Entity-Controlling_person_address_postal")}</PostalCode>
			</CurrentAddress>
			
			<MailingAddress>
				<Street>${getValue("SC-Entity-Controlling_person_mailing_address_street")}</Street>
				<City>${getValue("SC-Entity-Controlling_person_mailing_address_city")}</City>
				<State>${getValue("SC-Entity-Controlling_person_mailing_address_state")}</State>
				<Country>${getValue("SC-Entity-Controlling_person_mailing_address_country")}</Country>
				<PostalCode>${getValue("SC-Entity-Controlling_person_mailing_address_postal")}</PostalCode>
			</MailingAddress>
			
			<ControllingPersonType>
				<IsOwner>${convertYesNoToBoolean("SC-Entity-Controlling_person_type_owner")}</IsOwner>
				<OwnershipPercentage>${getValue("SC-Entity-Controlling_person_owner_percentage")}</OwnershipPercentage>
				<ByOtherMeans>${convertYesNoToBoolean("SC-Entity-Controlling_person_by_other_means")}</ByOtherMeans>
				<SeniorManaging>${convertYesNoToBoolean("SC-Entity-Controlling_person_senior_managing")}</SeniorManaging>
				
				<!-- Trust Options -->
				<TrustSettlor>${convertYesNoToBoolean("SC-Entity-Controlling_person_settlor")}</TrustSettlor>
				<TrustTrustee>${convertYesNoToBoolean("SC-Entity-Controlling_person_trustee")}</TrustTrustee>
				<TrustProtector>${convertYesNoToBoolean("SC-Entity-Controlling_person_protector")}</TrustProtector>
				<TrustBeneficiary>${convertYesNoToBoolean("SC-Entity-Controlling_person_beneficiary")}</TrustBeneficiary>
				<TrustOther>${convertYesNoToBoolean("SC-Entity-Controlling_person_other")}</TrustOther>
				<TrustOtherSpecify>${getValue("SC-Entity-Controlling_person_other_specify")}</TrustOtherSpecify>
				
				<!-- Legal Arrangement Options -->
				<SettlorEquivalent>${convertYesNoToBoolean("SC-Entity-Controlling_person_settlor_equivalent")}</SettlorEquivalent>
				<TrusteeEquivalent>${convertYesNoToBoolean("SC-Entity-Controlling_person_trustee_equivalent")}</TrusteeEquivalent>
				<ProtectorEquivalent>${convertYesNoToBoolean("SC-Entity-Controlling_person_protector_equivalent")}</ProtectorEquivalent>
				<BeneficiaryEquivalent>${convertYesNoToBoolean("SC-Entity-Controlling_person_beneficiary_equivalent")}</BeneficiaryEquivalent>
				<OtherEquivalent>${convertYesNoToBoolean("SC-Entity-Controlling_person_other_equivalent")}</OtherEquivalent>
				<OtherEquivalentSpecify>${getValue("SC-Entity-Controlling_person_other_equivalent_specify")}</OtherEquivalentSpecify>
			</ControllingPersonType>
			
			<TaxResidency>
				<Jurisdiction1>
					<Country>${getValue("SC-Entity-Controlling_person_jurisdiction_1")}</Country>
					<TIN>${getValue("SC-Entity-Controlling_person_tax_id_1")}</TIN>
					<TINType>${getValue("SC-Entity-Controlling_person_tax_reference_type_1")}</TINType>
					<TINNotAvailable>${convertYesNoToBoolean("SC-Entity-Controlling_person_tin_not_available_1")}</TINNotAvailable>
					<TINNotAvailableReason>${getValue("SC-Entity-Controlling_person_tin_reason_1")}</TINNotAvailableReason>
				</Jurisdiction1>
				
				<Jurisdiction2>
					<Country>${getValue("SC-Entity-Controlling_person_jurisdiction_2")}</Country>
					<TIN>${getValue("SC-Entity-Controlling_person_tax_id_2")}</TIN>
					<TINType>${getValue("SC-Entity-Controlling_person_tax_reference_type_2")}</TINType>
					<TINNotAvailableReason>${getValue("SC-Entity-Controlling_person_tin_reason_2")}</TINNotAvailableReason>
				</Jurisdiction2>
				
				<Jurisdiction3>
					<Country>${getValue("SC-Entity-Controlling_person_jurisdiction_3")}</Country>
					<TIN>${getValue("SC-Entity-Controlling_person_tax_id_3")}</TIN>
					<TINType>${getValue("SC-Entity-Controlling_person_tax_reference_type_3")}</TINType>
					<TINNotAvailableReason>${getValue("SC-Entity-Controlling_person_tin_reason_3")}</TINNotAvailableReason>
				</Jurisdiction3>
				
				<Jurisdiction4>
					<Country>${getValue("SC-Entity-Controlling_person_jurisdiction_4")}</Country>
					<TIN>${getValue("SC-Entity-Controlling_person_tax_id_4")}</TIN>
					<TINType>${getValue("SC-Entity-Controlling_person_tax_reference_type_4")}</TINType>
					<TINNotAvailableReason>${getValue("SC-Entity-Controlling_person_tin_reason_4")}</TINNotAvailableReason>
				</Jurisdiction4>
			</TaxResidency>
		</PersonDetails>
	</ControllingPersons>

	<!-- Section IV - Declaration Fields -->
	<Declaration>
		<ConsentToInformationSharing>${convertYesNoToBoolean("sc_consent_to_information_sharing")}</ConsentToInformationSharing>
		<AgreeToInformChanges>${convertYesNoToBoolean("sc_agree_to_inform_changes")}</AgreeToInformChanges>
		<CertifyAccountHolder>${convertYesNoToBoolean("sc_certify_account_holder")}</CertifyAccountHolder>
		<DeclareStatementsCorrect>${convertYesNoToBoolean("sc_declare_statements_correct")}</DeclareStatementsCorrect>
	</Declaration>

	<!-- Section V - Signature -->
	<SignInfo>
		<IsSigned>${convertYesNoToBoolean("SC_Entity_isSigned")}</IsSigned>
		<IsModified>${convertYesNoToBoolean("SC_Entity_isModified")}</IsModified>
		<SignatureUnreadable>${convertYesNoToBoolean("SC_Entity_Section_4_Signature_unreadable")}</SignatureUnreadable>
		<Signature>${getValue("SC-Entity-Section_5_signature")}</Signature>
		<SignatureUnreadable>${convertYesNoToBoolean("Signature_unreadable")}</SignatureUnreadable>
		<By>${getValue("SC_Entity_Section_4_signer_printed_name")}</By>
		<Capacity>${getValue("SC_Entity_Section_4_capacity")}</Capacity>
		<CapacityOther>${getValue("SC-Entity-Section_5_capacity_other")}</CapacityOther>
		<Date>${formatDateForXML(getValue("SC_Entity_Section_4_signature_date"))}</Date>
		<DateOnForm>${formatDateForXML(getValue("SC-Entity-Section_5_signature_date"))}</DateOnForm>
		<Certify>${convertYesNoToBoolean("certify")}</Certify>
	</SignInfo>

	${originalFileXml}
	
</Deloitte_US_Entity_2024>`;

  console.log("🎯 generateSelfCertEntityXML final XML:", xml);
  return xml;
}
